/**
 * DeleteAccountPage.js — public account-deletion request page
 * (Google Play Data Safety requirement: a web URL where users can
 * request deletion of their account and data).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, ShieldCheck, Clock, Mail } from 'lucide-react';

export default function DeleteAccountPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="delete-account-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3" data-testid="delete-account-back">
          <ArrowLeft size={14} /> Back to HiBFF
        </Link>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-1 flex items-center gap-2">
          <Trash2 className="text-red-500" size={28} /> Delete Your HiBFF Account
        </h1>
        <p className="text-sm text-gray-500 mb-8">HIBFF LTD · How to request deletion of your account and data · Applies to the HiBFF app (com.hibff.coach) and hibff.com</p>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Delete it yourself — takes under a minute</h2>
          <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
            <li><Link to="/login" className="text-red-600 font-semibold underline" data-testid="delete-account-login-link">Log in</Link> to your parent (account owner) account</li>
            <li>Open <strong>Account &amp; Settings</strong> from the avatar menu</li>
            <li>Scroll to the red <strong>Delete Account</strong> section</li>
            <li>Type <strong>DELETE</strong> to confirm</li>
          </ol>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
            <Clock size={18} className="text-amber-600" /> What happens next — the 90-day safety window
          </h2>
          <div className="text-sm text-gray-600 leading-relaxed space-y-2">
            <p>• Your subscription is <strong>cancelled immediately</strong> — no further billing.</p>
            <p>• Your account and any linked teen accounts become <strong>read-only</strong>; AI companions are switched off.</p>
            <p>• Nothing is destroyed straight away: for 90 days you can reinstate everything with one tap (this protects families from accidental or impulsive deletion).</p>
            <p>• Before day 90 we contact you by email and SMS so you can <strong>reinstate</strong>, keep your memories on an <strong>indefinite pause</strong>, or confirm <strong>permanent deletion of everything</strong>.</p>
            <p>• On confirmation, we permanently erase your account, linked teen accounts, conversations, trophies, wallet history and personal data, subject only to records we must keep by law (e.g. payment/tax records).</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
            <Mail size={18} className="text-sky-600" /> Can&apos;t log in?
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            Email{' '}
            <a href="mailto:privacy@hibff.com?subject=Account%20deletion%20request" className="text-sky-700 font-semibold underline" data-testid="delete-account-email">
              privacy@hibff.com
            </a>{' '}
            from the address registered on your account with the subject &quot;Account deletion
            request&quot;. We will verify your identity and process the request under the same
            90-day lifecycle above.
          </p>
        </div>

        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5 flex items-start gap-3">
          <ShieldCheck size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-800">
            Teen accounts are managed by the parent/guardian account owner. Full details of what we
            store and why are in our <a href="/privacy" className="underline font-semibold">Privacy &amp; Data Safety Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
